import { configBaseSelector, type ReferencedConfigInputType, useFetchConfigDetails } from '@repo/config-services';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { type BaseOperatorType } from 'advanced-filters/data';
import { getLeadAttributes, type GetLeadAttributesType } from 'advanced-filters/queries';
import { filterActions } from 'advanced-filters/store';
import { type FilterLevel } from 'advanced-filters/types/filter';
import { transformIncomingFilterData, updateFilterConditionsBasedOnConfigDetails } from 'advanced-filters/utils';
import { useLazyGetCriteriaQuery } from 'crm-atoms/services';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareCallback } from 'use-deep-compare';

export function useClientReadableCriteria(
  criteriaId?: number,
  saveInAdvancedFilter: boolean = false,
) {
  const dispatch = useDispatch();
  const [fetchCriteriaTrigger] = useLazyGetCriteriaQuery();
  const configData = useSelector(configBaseSelector);
  const [filterApplied, setFilterApplied] = useState(false);
  const { data } = useGetGraphqlDataQuery<UseQueryResult<GetLeadAttributesType>>({
    document: getLeadAttributes,
  });
  const attributes = data?.leadAttributes;

  const [appliedFilters, setAppliedFilters] = useState<{
    levels: FilterLevel[],
    comparator: BaseOperatorType,
  }>();
  const [referencedList, setReferencedList] = useState<ReferencedConfigInputType>({
    leadAttributes: [],
    users: [],
    activities: [],
    salesGroups: [],
    tasks: [],
    leadAttributeOptions: [],
    products: [],
    dealStatuses: [],
  });

  const modifyFilter = useDeepCompareCallback((
    levels: FilterLevel[],
    comparator: BaseOperatorType,
  ) => {
    if (saveInAdvancedFilter) dispatch(filterActions.setFilter({ levels, comparator }));
    setAppliedFilters({ levels, comparator });
  }, [dispatch, saveInAdvancedFilter]);

  const callbackToUpdateFilterWithConfigInfo = useDeepCompareCallback(() => {
    if (appliedFilters && appliedFilters.levels.length > 0) {
      const updatedLevels = updateFilterConditionsBasedOnConfigDetails(
        configData,
        appliedFilters.levels,
      ) as FilterLevel[];

      modifyFilter(updatedLevels, appliedFilters.comparator);
    }
  }, [appliedFilters, configData, modifyFilter]);

  useFetchConfigDetails(referencedList, callbackToUpdateFilterWithConfigInfo);

  const handleApplyFilter = useCallback(async () => {
    if (!criteriaId) {
      setReferencedList({
        leadAttributes: [],
        users: [],
        activities: [],
        salesGroups: [],
        tasks: [],
        leadAttributeOptions: [],
        products: [],
        dealStatuses: [],
      });
      setAppliedFilters(undefined);
      return;
    }
    setFilterApplied(false);

    try {
      const result = await fetchCriteriaTrigger([criteriaId], true).unwrap();
      const criteria = result.criteriaGroup?.[0];
      if (criteria) {
        const transformedData = transformIncomingFilterData(criteria, attributes ?? []);
        setReferencedList(transformedData.referencedData);
        modifyFilter(transformedData.buckets, transformedData.comparator);
        setFilterApplied(true);
      }
    } catch (error) {
      message.error('Something went wrong');
    }
  }, [criteriaId, fetchCriteriaTrigger, modifyFilter, attributes]);

  return {
    handleApplyFilter,
    appliedFilters,
    filterApplied,
  };
}
